import { supabase } from './supabase';

export interface BookingDiscount {
  id: string;
  booking_id: string;
  title: string;
  type: 'percentage' | 'fixed';
  amount: number;
  created_at: string;
  updated_at: string;
}

export async function applyDiscount(bookingId: string, name: string, type: 'percentage' | 'fixed', amount: number) {
  if (!bookingId) {
    throw new Error('Booking ID is required');
  }

  if (!name.trim()) {
    throw new Error('Discount name is required');
  }

  if (amount <= 0) {
    throw new Error('Amount must be greater than 0');
  }

  if (type === 'percentage' && amount > 100) {
    throw new Error('Percentage cannot exceed 100%');
  }

  // Remove any existing discounts from this booking (enforce single discount rule)
  const { error: removeError } = await supabase
    .from('booking_discounts')
    .delete()
    .eq('booking_id', bookingId);

  if (removeError) {
    console.error('Error removing existing discounts:', removeError);
    throw new Error('Failed to remove existing discount. Please try again.');
  }

  // Insert the discount (triggers will automatically update discount_amount, tax_amount, and total_amount)
  const { data: discount, error: discountError } = await supabase
    .from('booking_discounts')
    .insert([
      {
        booking_id: bookingId,
        title: name.trim(),
        type,
        amount
      }
    ])
    .select()
    .single();

  if (discountError) {
    console.error('Error applying discount:', discountError);
    throw new Error('Failed to apply discount. Please try again.');
  }

  // No need to manually update total_amount - our triggers handle it automatically:
  // Step 2 trigger: discount_amount updates
  // Step 3 trigger: tax_amount recalculates (on new taxable amount)
  // Step 4 trigger: total_amount recalculates

  return discount;
}

export async function deleteDiscount(id: string) {
  if (!id) {
    throw new Error('Discount ID is required');
  }

  // Delete the discount (triggers will automatically update discount_amount, tax_amount, and total_amount)
  const { error: deleteError } = await supabase
    .from('booking_discounts')
    .delete()
    .eq('id', id);

  if (deleteError) {
    console.error('Error deleting discount:', deleteError);
    throw new Error('Failed to delete discount');
  }

  // No need to manually update total_amount - our triggers handle it automatically:
  // Step 2 trigger: discount_amount resets to 0
  // Step 3 trigger: tax_amount recalculates (on full subtotal)
  // Step 4 trigger: total_amount recalculates
}